import { useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, FileXls, FilePdf, FileCode, Funnel, ArrowsClockwise, CaretDown as CaretDownIcon } from "@phosphor-icons/react";
import { api, API, fmtINR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";

const PNL_ROLES = new Set([
  "admin", "director", "finance", "accounts",
  "ho_approver", "project_head", "centre_coordinator",
]);

const reports = [
  { title: "Centre-wise Expense Report", desc: "All expenses across statuses per centre.", path: "/reports/expenses.xlsx", type: "xlsx", supports: ["centre", "status", "dates"] },
  { title: "Centre-wise Expense Report (PDF)", desc: "Print-ready summary.", path: "/reports/expenses.pdf", type: "pdf", supports: ["centre", "dates"] },
  { title: "Centre-wise Assets Report", desc: "Every capital asset with purchase date, cost & depreciation basis.", path: "/reports/assets.xlsx", type: "xlsx", supports: ["centre", "status", "dates"] },
  { title: "Vendor Spend Report", desc: "Vendor master (GSTIN, PAN, bank details) with total billed & net payable per vendor.", path: "/reports/vendors.xlsx", type: "xlsx", supports: ["centre", "dates"] },
  { title: "Centre-wise P&L", desc: "Consolidated income, expense and profit by centre.", path: "/reports/pnl.xlsx", type: "xlsx", supports: [], hideFor: ["centre_manager"] },
  { title: "Pending Approvals", desc: "Filtered expense report — submitted state.", path: "/reports/expenses.xlsx?status=submitted", type: "xlsx", supports: ["centre", "dates"] },
  { title: "Pending Payments", desc: "Filtered expense report — approved state.", path: "/reports/expenses.xlsx?status=approved", type: "xlsx", supports: ["centre", "dates"] },
  { title: "Paid Expense Register", desc: "Filtered expense report — paid state.", path: "/reports/expenses.xlsx?status=paid", type: "xlsx", supports: ["centre", "dates"] },
  { title: "Tally Prime Voucher Import", desc: "XML file of all Paid expenses formatted as Payment vouchers for direct import into Tally Prime.", path: "/reports/tally.xml", type: "xml", supports: ["centre", "dates"] },
];

const today = () => new Date().toISOString().slice(0, 10);
const monthStart = () => {
  const d = new Date(); d.setDate(1);
  return d.toISOString().slice(0, 10);
};

export default function Reports() {
  const { user } = useAuth();
  const [centres, setCentres] = useState([]);
  // `centre` is either "all" (no filter) or a comma-separated set of ids for multi
  const [flt, setFlt] = useState({ centre: "all", project: "all", start_date: "", end_date: "" });
  const visibleReports = reports.filter(
    (r) => !r.hideFor || !r.hideFor.includes(user?.role)
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const canSeeCompanyPnl = PNL_ROLES.has(user?.role);
  const tab = searchParams.get("tab") === "company" && canSeeCompanyPnl ? "company" : "downloads";

  useEffect(() => {
    if (user && user.role === "centre_manager") return;
    api.get("/centres").then((r) => setCentres(r.data));
  }, [user]);

  if (user && user.role === "centre_manager") {
    return <Navigate to="/dashboard" replace />;
  }

  const buildUrl = (r) => {
    const hasQ = r.path.includes("?");
    const params = new URLSearchParams(hasQ ? r.path.split("?")[1] : "");
    if (r.supports.includes("centre")) {
      const ids = effectiveCentreIds();
      if (ids.length === 1) {
        params.set("centre_id", ids[0]);
      } else if (ids.length > 1) {
        ids.forEach((id) => params.append("centre_ids", id));
      }
    }
    if (r.supports.includes("dates")) {
      if (flt.start_date) params.set("start_date", flt.start_date);
      if (flt.end_date) params.set("end_date", flt.end_date);
    }
    const base = hasQ ? r.path.split("?")[0] : r.path;
    const qs = params.toString();
    return `${API}${base}${qs ? `?${qs}` : ""}`;
  };

  const toggleCentre = (id) => {
    const cur = flt.centre === "all" ? [] : String(flt.centre).split(",").filter(Boolean);
    const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
    setFlt({ ...flt, centre: next.length ? next.join(",") : "all" });
  };
  const selectedCentres = flt.centre === "all" ? [] : String(flt.centre).split(",");

  const applyThisMonth = () => setFlt((f) => ({ ...f, start_date: monthStart(), end_date: today() }));
  const applyLastQuarter = () => {
    const end = new Date();
    const start = new Date(); start.setMonth(start.getMonth() - 3);
    setFlt((f) => ({ ...f, start_date: start.toISOString().slice(0, 10), end_date: end.toISOString().slice(0, 10) }));
  };
  const applyFY = () => {
    // Indian fiscal year: Apr-1 → Mar-31
    const now = new Date();
    const y = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    setFlt((f) => ({ ...f, start_date: `${y}-04-01`, end_date: `${y + 1}-03-31` }));
  };
  const clear = () => setFlt({ centre: "all", project: "all", start_date: "", end_date: "" });

  const activeFilters = [];
  if (flt.centre !== "all") activeFilters.push("centre");
  if (flt.project !== "all") activeFilters.push("project");
  if (flt.start_date || flt.end_date) activeFilters.push("date");

  const projectCentreIds = flt.project === "all"
    ? null
    : centres.filter((c) => (c.project_name || "Unassigned") === flt.project).map((c) => c.id);
  const effectiveCentreIds = () => {
    // Project filter takes precedence when explicit centre selection is not made
    if (flt.centre !== "all") return String(flt.centre).split(",").filter(Boolean);
    if (projectCentreIds) return projectCentreIds;
    return [];
  };

  return (
    <div className="p-8 space-y-6" data-testid="reports-root">
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">Exports</div>
        <h1 className="font-heading text-4xl mt-1 tracking-tight">Reports</h1>
        <p className="text-sm text-zinc-500 mt-1">Download Excel / PDF exports — filter by centre and date range.</p>
      </div>

      {/* Tabs: Downloads (exports) vs Company P&L (live rollup) */}
      <div className="flex gap-1 border-b border-zinc-200" data-testid="reports-tabs">
        {[
          { key: "downloads", label: "Downloads" },
          ...(canSeeCompanyPnl ? [{ key: "company", label: "Company P&L" }] : []),
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setSearchParams(t.key === "downloads" ? {} : { tab: t.key })}
            data-testid={`reports-tab-${t.key}`}
            className={`px-4 py-2 text-xs uppercase tracking-widest border-b-2 -mb-px transition-colors ${
              tab === t.key
                ? "border-zinc-900 text-zinc-900 font-medium"
                : "border-transparent text-zinc-500 hover:text-zinc-900"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "company" ? <CompanyPnL /> : (<>

      {/* Filters panel */}
      <div className="bg-white border border-zinc-200 p-6" data-testid="reports-filters">
        <div className="flex items-center gap-2 mb-4">
          <Funnel size={14} className="text-zinc-500" />
          <div className="text-xs uppercase tracking-widest text-zinc-500">Filters</div>
          {activeFilters.length > 0 && (
            <span className="text-[10px] uppercase tracking-widest bg-zinc-100 text-zinc-700 px-2 py-0.5">
              {activeFilters.length} active
            </span>
          )}
          <button onClick={clear} data-testid="reports-clear"
            className="ml-auto text-[10px] uppercase tracking-widest text-zinc-500 hover:text-black inline-flex items-center gap-1">
            <ArrowsClockwise size={10} /> Clear
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-widest">Project</Label>
            <select
              value={flt.project}
              onChange={(e) => setFlt({ ...flt, project: e.target.value })}
              className="h-10 border border-zinc-300 rounded-sm text-sm px-3 bg-white mt-2 w-full"
              data-testid="reports-project-filter"
            >
              <option value="all">All Projects</option>
              {Array.from(new Set(centres.map((c) => c.project_name || "Unassigned"))).sort().map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Centres</Label>
            <details className="rounded-sm border border-zinc-300 mt-2 bg-white" data-testid="reports-centre-multi">
              <summary className="h-10 px-3 flex items-center cursor-pointer text-sm select-none">
                {selectedCentres.length === 0
                  ? <span className="text-zinc-500">All Centres</span>
                  : <span>{selectedCentres.length} selected</span>}
                <CaretDownIcon className="ml-auto text-zinc-400" />
              </summary>
              <div className="max-h-60 overflow-y-auto border-t border-zinc-200">
                <label className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-zinc-50 cursor-pointer">
                  <input
                    type="radio" checked={flt.centre === "all"}
                    onChange={() => setFlt({ ...flt, centre: "all" })}
                    data-testid="reports-centre-all"
                  />
                  <span className="text-zinc-700">All Centres</span>
                </label>
                <div className="border-t border-zinc-100" />
                {centres.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-zinc-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedCentres.includes(c.id)}
                      onChange={() => toggleCentre(c.id)}
                      data-testid={`reports-centre-${c.code}`}
                    />
                    <span className="font-mono text-xs text-zinc-500">{c.code}</span>
                    <span>{c.name}</span>
                  </label>
                ))}
              </div>
            </details>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Start Date</Label>
            <Input type="date" value={flt.start_date}
              onChange={(e) => setFlt({ ...flt, start_date: e.target.value })}
              className="rounded-sm h-10 mt-2" data-testid="reports-start-date" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">End Date</Label>
            <Input type="date" value={flt.end_date}
              onChange={(e) => setFlt({ ...flt, end_date: e.target.value })}
              className="rounded-sm h-10 mt-2" data-testid="reports-end-date" />
          </div>
          <div className="flex items-end gap-2 flex-wrap">
            <button onClick={applyThisMonth} data-testid="preset-month"
              className="text-xs px-2.5 h-8 border border-zinc-300 hover:bg-zinc-100 rounded-sm">This Month</button>
            <button onClick={applyLastQuarter} data-testid="preset-quarter"
              className="text-xs px-2.5 h-8 border border-zinc-300 hover:bg-zinc-100 rounded-sm">Last 3 mo</button>
            <button onClick={applyFY} data-testid="preset-fy"
              className="text-xs px-2.5 h-8 border border-zinc-300 hover:bg-zinc-100 rounded-sm">This FY</button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {visibleReports.map((r) => {
          const filterable = r.supports.length > 0;
          return (
            <div key={r.title} className="bg-white border border-zinc-200 p-6 flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  {r.type === "pdf" ? <FilePdf size={18} weight="regular" /> :
                   r.type === "xml" ? <FileCode size={18} weight="regular" /> :
                   <FileXls size={18} weight="regular" />}
                  <div className="font-heading text-lg">{r.title}</div>
                </div>
                <div className="text-sm text-zinc-500 mt-1">{r.desc}</div>
                <div className="mt-2 flex gap-1 flex-wrap">
                  {r.supports.map((s) => (
                    <span key={s} className="text-[9px] uppercase tracking-widest bg-zinc-100 text-zinc-600 px-1.5 py-0.5">
                      {s === "centre" ? "centre-scoped" : s === "dates" ? "date-range" : s}
                    </span>
                  ))}
                  {!filterable && (
                    <span className="text-[9px] uppercase tracking-widest bg-zinc-100 text-zinc-400 px-1.5 py-0.5">
                      no filters
                    </span>
                  )}
                </div>
              </div>
              <a href={buildUrl(r)} target="_blank" rel="noreferrer">
                <Button className="rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white gap-2"
                  data-testid={`download-${r.title.replace(/\s|&|\(|\)/g,"-").toLowerCase()}`}>
                  <Download size={14} /> Download
                </Button>
              </a>
            </div>
          );
        })}
      </div>
      </>)}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Company P&L rollup — live aggregation of centre P&L grouped by company.
// Data: GET /api/dashboard/company-summary (respects caller's centre access).
// ---------------------------------------------------------------------------
function CompanyPnL() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/dashboard/company-summary")
      .then((r) => setGroups(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const totalIncome = groups.reduce((s, g) => s + g.income, 0);
  const totalExpense = groups.reduce((s, g) => s + g.expense, 0);
  const totalAssets = groups.reduce((s, g) => s + g.assets, 0);
  const chartData = groups.map((g) => ({ code: g.code, income: g.income, expense: g.expense }));

  if (loading) {
    return (
      <div className="p-12 text-center text-zinc-500 flex items-center justify-center gap-2" data-testid="company-pnl-loading">
        <span className="inline-block w-3 h-3 border-2 border-zinc-400 border-t-transparent rounded-full animate-spin" />
        Loading company rollup…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="company-pnl-root">
      <div className="flex justify-end">
        <a href={`${API}/reports/company-pnl.xlsx`} target="_blank" rel="noreferrer">
          <Button className="rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white gap-2"
            data-testid="download-company-pnl-xlsx">
            <Download size={14} /> Download Excel
          </Button>
        </a>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-6 bg-white border border-zinc-200">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Total Income</div>
          <div className="text-2xl font-heading mt-2 tabular-nums text-emerald-700" data-testid="company-pnl-total-income">{fmtINR(totalIncome)}</div>
        </div>
        <div className="p-6 bg-white border border-zinc-200">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Total Expense</div>
          <div className="text-2xl font-heading mt-2 tabular-nums" data-testid="company-pnl-total-expense">{fmtINR(totalExpense)}</div>
        </div>
        <div className="p-6 bg-white border border-zinc-200">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Total Assets</div>
          <div className="text-2xl font-heading mt-2 tabular-nums text-zinc-800" data-testid="company-pnl-total-assets">{fmtINR(totalAssets)}</div>
        </div>
        <div className="p-6 bg-white border border-zinc-200">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Consolidated P/L</div>
          <div className={`text-2xl font-heading mt-2 tabular-nums ${totalIncome - totalExpense >= 0 ? "text-emerald-700" : "text-rose-700"}`} data-testid="company-pnl-total-profit">
            {fmtINR(totalIncome - totalExpense)}
          </div>
        </div>
      </div>

      {groups.length > 0 && (
        <div className="bg-white border border-zinc-200 p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-4">Income vs Expense by Company</div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData}>
              <CartesianGrid stroke="#E4E4E7" strokeDasharray="3 3" />
              <XAxis dataKey="code" stroke="#71717A" fontSize={11} />
              <YAxis stroke="#71717A" fontSize={11} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => fmtINR(v)} contentStyle={{ border: "1px solid #00414E", borderRadius: 0, background: "#fff" }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="income" fill="#059669" />
              <Bar dataKey="expense" fill="#00414E" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {groups.length === 0 ? (
        <div className="bg-white border border-zinc-200 p-12 text-center text-sm text-zinc-500" data-testid="company-pnl-empty">
          No centres in your scope yet.
        </div>
      ) : (
        groups.map((g) => (
          <div key={g.company_id || "__unassigned__"} className="bg-white border border-zinc-200 overflow-x-auto"
            data-testid={`company-pnl-group-${g.company_id ? g.code : "unassigned"}`}>
            <div className="flex items-center justify-between px-4 py-3 bg-zinc-50 border-b border-zinc-200 flex-wrap gap-2">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Company</div>
                <div className="font-heading text-lg mt-0.5" data-testid={`company-pnl-name-${g.company_id ? g.code : "unassigned"}`}>
                  {g.name} <span className="font-mono text-xs text-zinc-500">({g.code})</span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                  {g.centre_count} centre{g.centre_count === 1 ? "" : "s"} · profit / loss
                </div>
                <div className={`font-heading text-base tabular-nums mt-0.5 ${g.profit >= 0 ? "text-emerald-700" : "text-rose-700"}`}
                  data-testid={`company-pnl-profit-${g.company_id ? g.code : "unassigned"}`}>
                  {fmtINR(g.profit)}
                </div>
              </div>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-[10px] uppercase tracking-widest text-zinc-500">
                <tr>
                  <th className="text-left p-4">Code</th>
                  <th className="text-left p-4">Centre</th>
                  <th className="text-left p-4">Project</th>
                  <th className="text-right p-4">Income</th>
                  <th className="text-right p-4">Expense</th>
                  <th className="text-right p-4">Assets</th>
                  <th className="text-right p-4">Profit / Loss</th>
                  <th className="text-right p-4">Annual Budget</th>
                  <th className="text-right p-4">Utilisation</th>
                </tr>
              </thead>
              <tbody className="tabular-nums">
                {g.centres.map((c) => (
                  <tr key={c.centre_id} className="border-t border-zinc-100 hover:bg-zinc-50"
                    data-testid={`company-pnl-centre-${c.code}`}>
                    <td className="p-4 font-mono text-xs">{c.code}</td>
                    <td className="p-4 font-medium">{c.name}</td>
                    <td className="p-4 text-zinc-600">{c.project_name || <span className="text-zinc-400">Unassigned</span>}</td>
                    <td className="p-4 text-right">{fmtINR(c.income)}</td>
                    <td className="p-4 text-right">{fmtINR(c.expense)}</td>
                    <td className="p-4 text-right text-zinc-700">{fmtINR(c.assets || 0)}</td>
                    <td className={`p-4 text-right font-medium ${c.profit >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{fmtINR(c.profit)}</td>
                    <td className="p-4 text-right">{fmtINR(c.annual_budget)}</td>
                    <td className="p-4 text-right">{c.utilisation}%</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-zinc-300 bg-zinc-50 font-medium"
                  data-testid={`company-pnl-total-${g.company_id ? g.code : "unassigned"}`}>
                  <td className="p-4" colSpan={3}>Company total</td>
                  <td className="p-4 text-right">{fmtINR(g.income)}</td>
                  <td className="p-4 text-right">{fmtINR(g.expense)}</td>
                  <td className="p-4 text-right">{fmtINR(g.assets)}</td>
                  <td className={`p-4 text-right ${g.profit >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{fmtINR(g.profit)}</td>
                  <td className="p-4 text-right">{fmtINR(g.annual_budget)}</td>
                  <td className="p-4 text-right">{g.utilisation}%</td>
                </tr>
              </tbody>
            </table>
          </div>
        ))
      )}
    </div>
  );
}
